import { useState, useEffect, useCallback } from "react";
import { Search, Download, Loader2, X, Instagram, Phone, Mail, CalendarCheck, Pencil, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { STATUS_LABELS, STATUS_COLORS } from "../../data/content";

const STATUSES = Object.keys(STATUS_LABELS);

function StatusBadge({ status }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-display uppercase tracking-wide" style={{ color: STATUS_COLORS[status] }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLORS[status] }} />
      {STATUS_LABELS[status]}
    </span>
  );
}

export default function LeadsTab({ onChange }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setEditForm({
      name: selected.name || "", email: selected.email || "", phone: selected.phone || "",
      instagram: selected.instagram || "", age: selected.age ?? "", sex: selected.sex || "",
      weight: selected.weight ?? "", height: selected.height ?? "", goal: selected.goal || "",
      blocker: selected.blocker || "", time_trying: selected.time_trying || "", why_now: selected.why_now || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm, age: parseInt(editForm.age, 10), weight: parseFloat(editForm.weight), height: parseFloat(editForm.height) };
      const { data } = await api.put(`/admin/leads/${selected.id}`, payload);
      setSelected(data.lead);
      setLeads((ls) => ls.map((l) => (l.id === data.lead.id ? data.lead : l)));
      setEditing(false);
      onChange?.();
      toast.success("Lead actualizado.");
    } catch (err) {
      toast.error(err.response?.data?.detail ? String(err.response.data.detail) : "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("¿Eliminar este lead? Si tiene una reserva, también se eliminará y la hora quedará libre. Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      setSelected(null);
      setEditing(false);
      onChange?.();
      toast.success("Lead eliminado.");
    } catch {
      toast.error("No se pudo eliminar el lead.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads", { params: { search: search || undefined, status: filter } });
      setLeads(data.leads);
    } catch {
      toast.error("Error al cargar leads.");
    } finally {
      setLoading(false);
    }
  }, [search, filter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      onChange?.();
      toast.success("Estado actualizado.");
    } catch {
      toast.error("No se pudo actualizar el estado.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/admin/leads/export`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("ec_admin_token")}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads_elite_coaching.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar.");
    }
  };

  const selectCls = "bg-black border border-white/15 rounded-sm px-3 py-2 text-xs text-white focus:border-[#D4AF37] focus:outline-none";

  return (
    <div data-testid="leads-tab">
      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="leads-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, teléfono o Instagram..."
            className="w-full bg-[#0A0A0A] border border-white/15 rounded-sm pl-9 pr-4 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#D4AF37] focus:outline-none"
          />
        </div>
        <select data-testid="leads-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className={selectCls + " py-2.5"}>
          <option value="all">Todos los estados</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
        <button data-testid="leads-export" onClick={exportCsv} className="btn-outline-gold rounded-sm px-4 py-2.5 text-xs inline-flex items-center gap-2 justify-center">
          <Download size={15} /> Exportar
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 text-white/40 text-sm py-16">
            <Loader2 className="animate-spin" size={16} /> Cargando...
          </div>
        ) : leads.length === 0 ? (
          <p className="text-center text-white/40 text-sm py-16">No hay leads que mostrar.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-white/40 font-display">
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3 hidden md:table-cell">Contacto</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Objetivo</th>
                  <th className="px-4 py-3">Reserva</th>
                  <th className="px-4 py-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    onClick={() => setSelected(l)}
                    className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">{l.name}</p>
                      <p className="text-white/40 text-xs md:hidden">{l.email}</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-white/60 text-xs">
                      <p>{l.email}</p>
                      <p>{l.phone}</p>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-white/60 text-xs capitalize">{(l.goal || "").replace("_", " ")}</td>
                    <td className="px-4 py-3 text-xs">
                      {l.booking ? (
                        <span className="text-[#34D399] inline-flex items-center gap-1"><CalendarCheck size={13} /> {l.booking.date} {l.booking.time}</span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <select
                        data-testid={`lead-status-${l.id}`}
                        value={l.status}
                        onChange={(e) => changeStatus(l.id, e.target.value)}
                        className={selectCls}
                        style={{ color: STATUS_COLORS[l.status] }}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s} className="text-white">{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setSelected(null); setEditing(false); }}>
          <div data-testid="lead-detail" className="bg-[#0A0A0A] border border-white/15 rounded-sm w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A0A0A] z-10">
              <div>
                <h3 className="font-display font-bold uppercase text-lg">{selected.name}</h3>
                <StatusBadge status={selected.status} />
              </div>
              <button data-testid="lead-detail-close" onClick={() => { setSelected(null); setEditing(false); }} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>

            {!editing ? (
              <div className="p-5 space-y-4 text-sm">
                <div className="grid grid-cols-1 gap-2">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-white/70 hover:text-[#D4AF37]"><Mail size={14} className="text-[#D4AF37]" /> {selected.email}</a>
                  <span className="flex items-center gap-2 text-white/70"><Phone size={14} className="text-[#D4AF37]" /> {selected.phone}</span>
                  {selected.instagram ? (
                    <span className="flex items-center gap-2 text-white/70"><Instagram size={14} className="text-[#D4AF37]" /> {selected.instagram}</span>
                  ) : null}
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  {[["Edad", selected.age], ["Sexo", selected.sex], ["Peso", `${selected.weight} kg`], ["Altura", `${selected.height} cm`], ["Objetivo", (selected.goal || "").replace("_", " ")]].map(([k, v]) => (
                    <div key={k} className="border border-white/10 rounded-sm p-3">
                      <p className="text-[10px] uppercase tracking-wide text-white/40">{k}</p>
                      <p className="text-white capitalize">{v}</p>
                    </div>
                  ))}
                </div>
                {selected.booking && (
                  <div className="border border-[#D4AF37]/40 rounded-sm p-3">
                    <p className="text-[10px] uppercase tracking-wide text-[#D4AF37]">Reserva</p>
                    <p className="text-white">{selected.booking.date_label} · {selected.booking.time}</p>
                  </div>
                )}
                <div className="space-y-3 pt-2">
                  {[["¿Qué te impide conseguir resultados?", selected.blocker], ["¿Cuánto tiempo lo intentas?", selected.time_trying], ["¿Por qué ahora?", selected.why_now]].map(([q, a]) => (
                    <div key={q}>
                      <p className="text-[11px] uppercase tracking-wide text-white/40 mb-1">{q}</p>
                      <p className="text-white/80">{a}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3 pt-3 border-t border-white/10">
                  <button data-testid="lead-edit-btn" onClick={startEdit} className="btn-outline-gold rounded-sm flex-1 py-2.5 text-xs inline-flex items-center justify-center gap-2"><Pencil size={14} /> Editar</button>
                  <button data-testid="lead-delete-btn" onClick={() => deleteLead(selected.id)} className="rounded-sm flex-1 py-2.5 text-xs inline-flex items-center justify-center gap-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors uppercase tracking-wide font-bold"><Trash2 size={14} /> Eliminar</button>
                </div>
              </div>
            ) : (
              <div className="p-5 space-y-3 text-sm" data-testid="lead-edit-form">
                {[["name", "Nombre", "text"], ["email", "Email", "email"], ["phone", "Teléfono", "text"], ["instagram", "Instagram", "text"], ["age", "Edad", "number"], ["weight", "Peso (kg)", "number"], ["height", "Altura (cm)", "number"]].map(([key, label, type]) => (
                  <div key={key}>
                    <label className="block text-[10px] uppercase tracking-wide text-white/40 mb-1">{label}</label>
                    <input data-testid={`edit-${key}`} type={type} value={editForm[key]} onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))} className="w-full bg-black border border-white/15 rounded-sm px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                ))}
                <div>
                  <label className="block text-[10px] uppercase tracking-wide text-white/40 mb-1">Sexo</label>
                  <select value={editForm.sex} onChange={(e) => setEditForm((f) => ({ ...f, sex: e.target.value }))} className="w-full bg-black border border-white/15 rounded-sm px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none">
                    <option value="hombre">Hombre</option>
                    <option value="mujer">Mujer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wide text-white/40 mb-1">Objetivo</label>
                  <select value={editForm.goal} onChange={(e) => setEditForm((f) => ({ ...f, goal: e.target.value }))} className="w-full bg-black border border-white/15 rounded-sm px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none">
                    <option value="perder_grasa">Perder grasa</option>
                    <option value="ganar_musculo">Ganar músculo</option>
                    <option value="recomposicion">Recomposición corporal</option>
                    <option value="mejorar_habitos">Mejorar hábitos</option>
                  </select>
                </div>
                {[["blocker", "¿Qué te impide conseguir resultados?"], ["time_trying", "¿Cuánto tiempo lo intentas?"], ["why_now", "¿Por qué ahora?"]].map(([key, label]) => (
                  <div key={key}>
                    <label className="block text-[10px] uppercase tracking-wide text-white/40 mb-1">{label}</label>
                    <textarea data-testid={`edit-${key}`} rows={2} value={editForm[key]} onChange={(e) => setEditForm((f) => ({ ...f, [key]: e.target.value }))} className="w-full bg-black border border-white/15 rounded-sm px-3 py-2 text-white focus:border-[#D4AF37] focus:outline-none" />
                  </div>
                ))}
                <div className="flex gap-3 pt-3 border-t border-white/10">
                  <button onClick={() => setEditing(false)} className="btn-outline-gold rounded-sm flex-1 py-2.5 text-xs">Cancelar</button>
                  <button data-testid="lead-save-btn" onClick={saveEdit} disabled={saving} className="btn-gold rounded-sm flex-1 py-2.5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Guardar</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
